import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import {
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { PlatformType } from '../../types/authorization'
import { fetchReportEditInit } from '../../api/dashboard'
import { resolveInitDetailRequest } from '../../lib/initDetail'
import { dashboardBranding } from '../../lib/productBranding'
import { useAuthSession } from '../../lib/session'
import { useToast } from '../../contexts/ToastContext'
import { useAuthorization } from '../../contexts/AuthorizationContext'
import {
  useDashboardDetailInit,
  useDashboardReports,
  usePowerBIReports,
  usePowerBIReportPages,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
} from '../../hooks/useDashboard'
import { DASHBOARD_LINKED } from '../../config/dashboardLinked'
import FeatureShell from '../../components/FeatureShell'
import type {
  DashboardReport,
  ReportTypeId,
  PowerBIReportOption,
  UserOption,
} from '../../types/dashboard'

type ModalState = {
  open: boolean
  mode: 'create' | 'edit'
  reportId: string
}

type FormValues = {
  name: string
  statusId: number
  typeId: ReportTypeId | null
  platformType: number | null
  powerBIReport: string
  powerBIReportPage: string
  rowLevelSecurity: boolean
  roleName: string
  secondRoleName: string
  groupIds: string[]
  userIds: string[]
}

const reportTypeOptions = [
  { value: 1 as ReportTypeId, label: 'Practice Intel' },
  { value: 2 as ReportTypeId, label: 'Advisory Intel' },
]

const reportPlatformTypeOptions = [
  { value: PlatformType.Xero, label: 'Xero' },
  { value: PlatformType.XeroXpm, label: 'XPM' },
  { value: PlatformType.Myob, label: 'MYOB' },
  { value: PlatformType.WorkGuru, label: 'WorkGuru' },
  { value: PlatformType.BRWFM, label: 'Bluerock Workflow Max' },
  { value: PlatformType.Karbon, label: 'Karbon' },
  { value: PlatformType.FYI, label: 'FYI' },
  { value: 0, label: 'Others' },
]

type ClientReportPageProps = {
  typeId?: ReportTypeId
  reportDetailBasePath?: string
}

function renderFieldLabel(label: string, isLoading: boolean, loadingText: string) {
  return (
    <span className="inline-flex items-center gap-2">
      <span>{label}</span>
      {isLoading && (
        <span className="inline-flex items-center gap-1 text-xs font-normal text-gray-500">
          <span className="feature-shell__spinner h-3 w-3 rounded-full border animate-spin" />
          <span>{loadingText}</span>
        </span>
      )}
    </span>
  )
}

function renderSelectAllMenu(
  label: string,
  values: string[],
  disabled: boolean,
  onChange: (values: string[]) => void
) {
  const hasOptions = values.length > 0

  return (
    <div className="py-1">
      <div className="px-2">
        <Space size={8}>
          <Button
            size="small"
            type="text"
            disabled={disabled || !hasOptions}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onChange(values)}
          >
            Select all {label}
          </Button>
          <Button
            size="small"
            type="text"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onChange([])}
          >
            Clear
          </Button>
        </Space>
      </div>
      <Divider className="my-2" />
    </div>
  )
}

export default function ClientReportPage({
  typeId: fixedTypeId,
  reportDetailBasePath,
}: ClientReportPageProps) {
  const session = useAuthSession()
  const initDetailRequest = resolveInitDetailRequest(session)
  const { clientId, hasPermission } = useAuthorization()
  const clientDisplayName = session?.currentClientName?.trim() || 'TBAC'
  const pageTypeId: ReportTypeId = fixedTypeId ?? 1
  const resolvedReportDetailBasePath = reportDetailBasePath ?? '/report'
  const branding = dashboardBranding[pageTypeId]
  const productLabel = branding.copy.productName
  const itemSingular = branding.copy.itemSingular
  const itemPlural = branding.copy.itemPlural
  const linkedAssetLabel = branding.copy.linkedAssetLabel ?? 'Dashboard linked'
  const screenTitle = branding.copy.screenTitle ?? productLabel
  const navigate = useNavigate()
  const { addToast } = useToast()
  const isAdmin = hasPermission('viewAdminUser')

  const [nameFilter, setNameFilter] = useState('')
  const [dashboardIdFilter, setDashboardIdFilter] = useState('')
  const [hideInactive, setHideInactive] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
  const [modalState, setModalState] = useState<ModalState>({ open: false, mode: 'create', reportId: '' })
  const [selectedPbiReport, setSelectedPbiReport] = useState('')
  const [isRlsDisabled, setIsRlsDisabled] = useState(false)
  const [editInit, setEditInit] = useState<Awaited<ReturnType<typeof fetchReportEditInit>> | null>(null)
  const [isEditFetching, setIsEditFetching] = useState(false)

  const [form] = Form.useForm<FormValues>()
  const watchRls = Form.useWatch('rowLevelSecurity', form)
  const watchPbiReport = Form.useWatch('powerBIReport', form)

  const { reports, groupOptions, isLoading, isFetching, refetch } = useDashboardReports(initDetailRequest, {
    name: '',
    powerBIReportName: null,
  })
  const {
    data: detailInit,
    isLoading: isDetailInitLoading,
    isFetching: isDetailInitFetching,
  } = useDashboardDetailInit(initDetailRequest)

  const {
    data: pbiReports = [],
    isLoading: isPbiReportsLoading,
    isFetching: isPbiReportsFetching,
  } = usePowerBIReports(clientId)
  const {
    data: pbiPages = [],
    isLoading: isPbiPagesLoading,
    isFetching: isPbiPagesFetching,
  } = usePowerBIReportPages(
    selectedPbiReport,
    !!selectedPbiReport
  )

  const createMutation = useCreateReportMutation(clientId)
  const updateMutation = useUpdateReportMutation(clientId)
  const deleteMutation = useDeleteReportMutation(clientId)

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      if (report.typeId !== pageTypeId) return false
      if (hideInactive && report.status !== 'Active') return false

      const trimmedName = nameFilter.trim().toLowerCase()
      if (trimmedName && !report.name.toLowerCase().includes(trimmedName)) return false

      const trimmedDashboardId = dashboardIdFilter.trim().toLowerCase()
      if (trimmedDashboardId && !report.powerBIReportName.toLowerCase().includes(trimmedDashboardId)) {
        return false
      }

      return true
    })
  }, [reports, pageTypeId, hideInactive, nameFilter, dashboardIdFilter])
  const directUserOptions = editInit?.userOptions?.length ? editInit.userOptions : detailInit?.userOptions ?? []
  const sortedDirectUserOptions = useMemo(() => {
    if (modalState.mode !== 'edit') return directUserOptions

    return [...directUserOptions].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  }, [directUserOptions, modalState.mode])
  const dashboardLinkedOptions = editInit?.dashboardLinkedOptions?.length ? editInit.dashboardLinkedOptions : DASHBOARD_LINKED
  const availablePages = useMemo(() => {
    if (modalState.mode === 'edit' && editInit?.powerbiReportPages?.length) {
      const activeReportId = selectedPbiReport || watchPbiReport || editInit.report.powerBIReport
      const hasScopedPages = editInit.powerbiReportPages.some((p) => p.reportId)
      const editPages = hasScopedPages
        ? editInit.powerbiReportPages.filter((p) => p.reportId === activeReportId)
        : editInit.powerbiReportPages

      if (editPages.length > 0) {
        return editPages
      }
    }
    return pbiPages
  }, [editInit, modalState.mode, pbiPages, selectedPbiReport, watchPbiReport])

  // Populate form when edit data loads
  useEffect(() => {
    if (modalState.mode !== 'edit' || !editInit) return
    const r = editInit.report
    let hasCustomRoleName = false

    form.setFieldsValue({
      name: r.name,
      statusId: r.statusId,
      typeId: r.typeId,
      platformType: r.platformType ?? 0,
      powerBIReport: r.powerBIReport,
      powerBIReportPage: r.powerBIReportPage,
      rowLevelSecurity: r.rowLevelSecurity,
      roleName: r.roleName || '',
      secondRoleName: r.secondRoleName || '',
      groupIds: r.groupIds,
      userIds: r.userIds || [],
    })
    setSelectedPbiReport(r.powerBIReport)

    // If roleName exists → RLS is on (legacy behavior)
    if (r.roleName) {
      form.setFieldsValue({ rowLevelSecurity: true })
      hasCustomRoleName = true
    }

    // Check if report matches dashboardLinked → disable toggle
    const linked = dashboardLinkedOptions.find((d) =>
      r.powerBIReportName?.toLowerCase().includes(d.name.toLowerCase())
    )
    if (linked) {
      setIsRlsDisabled(true)
      form.setFieldsValue({ rowLevelSecurity: true })
      if (!hasCustomRoleName) {
        form.setFieldsValue({ roleName: linked.roleName })
      }
    } else {
      setIsRlsDisabled(false)
    }
  }, [dashboardLinkedOptions, editInit, form, modalState.mode])

  // Once pages load, clear powerBIReportPage if its value no longer exists
  // in the current page options (the page may have been removed from the report).
  useEffect(() => {
    if (!availablePages.length || modalState.mode !== 'edit') return
    const currentPage = form.getFieldValue('powerBIReportPage') as string
    if (!currentPage) return
    const exists = availablePages.some((p) => p.value === currentPage || p.name === currentPage)
    if (!exists) {
      form.setFieldsValue({ powerBIReportPage: '' })
    }
  }, [availablePages, form, modalState.mode])

  // Clear roleName when RLS is unchecked (legacy behavior)
  useEffect(() => {
    if (watchRls === false) {
      form.setFieldsValue({ roleName: '', secondRoleName: '' })
    }
  }, [watchRls, form])

  const isBaseModalDataLoading =
    isLoading ||
    isFetching ||
    isDetailInitLoading ||
    isDetailInitFetching ||
    isPbiReportsLoading ||
    isPbiReportsFetching

  const validateModalPrerequisites = useCallback(() => {
    if (isBaseModalDataLoading) {
      addToast(`Please wait for ${productLabel} options to finish loading`, 'error')
      return false
    }

    if (!detailInit) {
      addToast(`${productLabel} options are not ready yet`, 'error')
      return false
    }

    return true
  }, [addToast, detailInit, isBaseModalDataLoading, productLabel])

  const openCreate = () => {
    if (!validateModalPrerequisites()) return

    form.resetFields()
    setEditInit(null)
    form.setFieldsValue({
      name: '',
      statusId: 1,
      typeId: fixedTypeId ?? 1,
      platformType: null,
      powerBIReport: '',
      powerBIReportPage: '',
      rowLevelSecurity: false,
      roleName: '',
      secondRoleName: '',
      groupIds: [],
      userIds: [],
    })
    setSelectedPbiReport('')
    setIsRlsDisabled(false)
    setModalState({ open: true, mode: 'create', reportId: '' })
  }

  const openEdit = useCallback(async (row: DashboardReport) => {
    if (!validateModalPrerequisites()) return

    setSelectedPbiReport('')
    setIsEditFetching(true)
    setModalState({ open: true, mode: 'edit', reportId: row.reportId })
    try {
      const data = await fetchReportEditInit(row.reportId)
      setEditInit(data)
    } catch (e) {
      setModalState({ open: false, mode: 'create', reportId: '' })
      addToast(e instanceof Error ? e.message : `Unable to load ${itemSingular} details`, 'error')
    } finally {
      setIsEditFetching(false)
    }
  }, [addToast, itemSingular, validateModalPrerequisites])

  const closeModal = () => {
    setModalState({ open: false, mode: 'create', reportId: '' })
    setEditInit(null)
    form.resetFields()
    setSelectedPbiReport('')
    setIsRlsDisabled(false)
  }

  const handlePbiReportChange = (value: string) => {
    setSelectedPbiReport(value)
    form.setFieldsValue({ powerBIReportPage: '' })

    const pbiOption = [...reportPbiOptions, ...pbiReports].find((p) => p.id === value)
    if (pbiOption) {
      form.setFieldsValue({ powerBIReport: value })
    }

    // Clear RLS state first (legacy behavior)
    form.setFieldsValue({ rowLevelSecurity: false, roleName: '', secondRoleName: '' })
    setIsRlsDisabled(false)

    // Then check dashboardLinked
    if (pbiOption) {
      const linked = dashboardLinkedOptions.find(
        (d) => d.name === pbiOption.name || pbiOption.name.includes(d.name)
      )
      if (linked) {
        setIsRlsDisabled(true)
        form.setFieldsValue({ rowLevelSecurity: true, roleName: linked.roleName })
      }
    }
  }

  const saveReport = async () => {
    try {
      const values = await form.validateFields()
      const pbiOption = [...reportPbiOptions, ...pbiReports].find((p) => p.id === values.powerBIReport)
      const payload = {
        clientId,
        name: values.name.trim(),
        statusId: values.statusId,
        powerBIReport: values.powerBIReport,
        powerBIReportName: pbiOption?.name || '',
        groupIds: values.groupIds || [],
        userIds: values.userIds || [],
        roleName: values.roleName || null,
        powerBIReportPage: values.powerBIReportPage || '',
        secondRoleName: values.secondRoleName || null,
        powerBIWorkspace: detailInit?.clientWorkspace || editInit?.report.powerBIWorkspace || editInit?.workspace || null,
        platformType: values.platformType ?? null,
        typeId: values.typeId,
      }

      if (modalState.mode === 'create') {
        await createMutation.mutateAsync(payload)
        addToast(`${itemSingular} created`, 'success')
      } else {
        await updateMutation.mutateAsync({ ...payload, reportId: modalState.reportId })
        addToast(`${itemSingular} updated`, 'success')
      }
      closeModal()
    } catch (e) {
      if (typeof e === 'object' && e !== null && 'errorFields' in e) return
      addToast(e instanceof Error ? e.message : `Unable to save ${itemSingular}`, 'error')
    }
  }

  const deleteReportHandler = () => {
    if (modalState.mode !== 'edit' || !modalState.reportId) return
    Modal.confirm({
      title: `Delete ${itemSingular}`,
      content: `Are you sure you want to delete this ${itemSingular}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(modalState.reportId)
          await refetch()
          addToast(`${itemSingular} deleted successfully`, 'success')
          closeModal()
        } catch (e) {
          addToast(e instanceof Error ? e.message : `Unable to delete ${itemSingular}`, 'error')
          throw e
        }
      },
    })
  }

  const onRowClick = (record: DashboardReport) => {
    navigate(`${resolvedReportDetailBasePath}/${record.reportId}`)
  }

  const columns: ColumnsType<DashboardReport> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_, row) => (
          <button
            type="button"
            onClick={() => onRowClick(row)}
            className="feature-link font-medium text-gray-900 text-left"
          >
            {row.name}
          </button>
        ),
      },
      ...(isAdmin
        ? [
            {
              title: 'Power BI Report',
              dataIndex: 'powerBIReportName' as const,
              key: 'powerBIReportName',
              sorter: (a: DashboardReport, b: DashboardReport) =>
                a.powerBIReportName.localeCompare(b.powerBIReportName),
            },
          ]
        : []),
      {
        title: 'User Groups',
        key: 'groups',
        render: (_: unknown, row: DashboardReport) => (
          <Space size={4} wrap>
            {row.groups.map((g) => (
              <Tag key={g} color="blue">
                {g}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        render: (_: unknown, row: DashboardReport) =>
          row.status === 'Active' ? (
            <Tag color="green">Active</Tag>
          ) : (
            <Tag color="default">Inactive</Tag>
          ),
      },
      ...(isAdmin
        ? [
            {
              title: '',
              key: 'action',
              width: 60,
              render: (_: unknown, row: DashboardReport) => (
                <Tooltip title={`Edit ${itemSingular}`}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    aria-label={`Edit ${itemSingular}`}
                    disabled={isBaseModalDataLoading}
                    onClick={(e) => {
                      e.stopPropagation()
                      openEdit(row)
                    }}
                  />
                </Tooltip>
              ),
            },
          ]
        : []),
    ],
    [isAdmin, isBaseModalDataLoading, itemSingular, openEdit] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const onTableChange = (nextPagination: TablePaginationConfig) => {
    setPagination({
      current: nextPagination.current ?? pagination.current,
      pageSize: nextPagination.pageSize ?? pagination.pageSize,
    })
  }

  // Get options for the edit modal selects — use .length to avoid empty-array truthy trap
  const statusOptions = editInit?.statusOptions?.length
    ? editInit.statusOptions
    : [
        { value: 1, name: 'Active' },
        { value: 2, name: 'Inactive' },
      ]
  // Filter PBI reports by typeId for create mode
  const filteredPbiReports = useMemo(() => {
    const typeId = form.getFieldValue('typeId') as ReportTypeId | null
    if (!typeId) return pbiReports
    // In real app, advisory reports would be filtered server-side
    return pbiReports
  }, [pbiReports, form])
  const reportGroupOptions = editInit?.userGroupOptions?.length ? editInit.userGroupOptions : groupOptions
  const reportPbiOptions = editInit?.powerbiReportOptions?.length ? editInit.powerbiReportOptions : filteredPbiReports
  const isReportOptionsLoading = isPbiReportsLoading || isPbiReportsFetching
  const isReportPageOptionsLoading = isPbiPagesLoading || isPbiPagesFetching
  const isGroupOptionsLoading = isBaseModalDataLoading && !editInit?.userGroupOptions?.length
  const isUserOptionsLoading = isBaseModalDataLoading && !editInit?.userOptions?.length
  const groupOptionValues = reportGroupOptions.map((group) => group.value)
  const directUserOptionValues = sortedDirectUserOptions.map((user) => user.value)

  return (
    <FeatureShell
      featureKey={branding.featureKey}
      withAntd
      glow
      bodyClassName="p-0"
      header={
        <>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">
                {screenTitle}
              </h1>
              <p className="text-sm text-gray-500 mt-1">Client: {clientDisplayName}</p>
            </div>
            <Space>
              {isAdmin && (
                <Button icon={<PlusOutlined />} onClick={openCreate} disabled={isBaseModalDataLoading}>
                  Add {itemSingular}
                </Button>
              )}
              <Tag className="feature-shell__count px-3 py-1 rounded-full font-medium border-0">
                {filteredReports.length} {itemPlural}
              </Tag>
            </Space>
          </div>
          <div className="feature-shell__accent mt-3 h-1.5 w-16 rounded-full" aria-hidden />
        </>
      }
    >
      <div className="p-4 bg-white/70 backdrop-blur-[1px]">
        <Space wrap size={12}>
          <Input
            placeholder="Search by Name"
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value)
              setPagination((prev) => ({ ...prev, current: 1 }))
            }}
            suffix={<SearchOutlined className="feature-shell__search-icon" />}
            style={{ width: 240 }}
            allowClear
          />
          <Input
            placeholder={`Search by ${itemSingular} ID`}
            value={dashboardIdFilter}
            onChange={(e) => {
              setDashboardIdFilter(e.target.value)
              setPagination((prev) => ({ ...prev, current: 1 }))
            }}
            suffix={<SearchOutlined className="feature-shell__search-icon" />}
            style={{ width: 240 }}
            allowClear
          />
          <Checkbox
            checked={hideInactive}
            onChange={(e) => {
              setHideInactive(e.target.checked)
              setPagination((prev) => ({ ...prev, current: 1 }))
            }}
          >
            Hide Inactive {itemPlural}
          </Checkbox>
          {(isLoading || isFetching) && (
            <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
              <span className="feature-shell__spinner w-4 h-4 border-2 rounded-full animate-spin" />
              Loading...
            </span>
          )}
        </Space>
      </div>

      <div className="p-4">
        <Table<DashboardReport>
          rowKey="id"
          columns={columns}
          dataSource={filteredReports}
          loading={isLoading}
          onChange={onTableChange}
          onRow={(record) => ({
            onClick: () => onRowClick(record),
            className: 'cursor-pointer',
          })}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredReports.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0] ?? 0} - ${range[1] ?? 0} of ${total}`,
          }}
        />
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={modalState.open}
        title={modalState.mode === 'create' ? `Add ${itemSingular}` : `Edit ${itemSingular}`}
        onCancel={closeModal}
        onOk={() => void saveReport()}
        okText={modalState.mode === 'create' ? 'Create' : 'Update'}
        okButtonProps={{
          loading: createMutation.isPending || updateMutation.isPending,
        }}
        width={640}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space>
            {modalState.mode === 'edit' && (
              <Button
                danger
                loading={deleteMutation.isPending}
                onClick={deleteReportHandler}
              >
                Delete
              </Button>
            )}
            <CancelBtn />
            <OkBtn />
          </Space>
        )}
      >
        {isEditFetching && modalState.mode === 'edit' ? (
          <div className="flex items-center justify-center py-8">
            <span className="feature-shell__spinner w-6 h-6 border-2 rounded-full animate-spin" />
            <span className="ml-2 text-gray-500">Loading {itemSingular} details...</span>
          </div>
        ) : (
          <Form form={form} layout="vertical">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="statusId"
                label="Status"
                rules={[{ required: true, message: 'Status is required' }]}
              >
                <Select
                  options={statusOptions.map((s) => ({ value: s.value, label: s.name }))}
                />
              </Form.Item>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Form.Item
                name="powerBIReport"
                label={renderFieldLabel(linkedAssetLabel, isReportOptionsLoading, 'Loading options')}
                rules={[{ required: true, message: `${linkedAssetLabel} is required` }]}
              >
                <Select
                  options={(modalState.mode === 'edit' ? reportPbiOptions : filteredPbiReports).map((p: PowerBIReportOption) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                  placeholder="Select PBI report"
                  showSearch
                  optionFilterProp="label"
                  onChange={handlePbiReportChange}
                  disabled={isReportOptionsLoading}
                  loading={isReportOptionsLoading}
                />
              </Form.Item>
              <Form.Item
                name="powerBIReportPage"
                label={renderFieldLabel('Default Report Page', isReportPageOptionsLoading, 'Loading pages')}
              >
                <Select
                  options={availablePages.map((p) => ({ value: p.value, label: p.name }))}
                  placeholder="Select page"
                  allowClear
                  disabled={!selectedPbiReport && !watchPbiReport || isReportPageOptionsLoading}
                  loading={isReportPageOptionsLoading}
                />
              </Form.Item>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Form.Item
                name="platformType"
                label="Platform Type"
                rules={[{ required: true, message: 'Platform type is required' }]}
              >
                <Select
                  options={reportPlatformTypeOptions}
                  placeholder="Select platform type"
                />
              </Form.Item>
              <Form.Item name="typeId" label="Product Type">
                <Select
                  options={reportTypeOptions}
                  placeholder="Select type"
                  allowClear
                  disabled={modalState.mode === 'edit'}
                />
              </Form.Item>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Form.Item name="rowLevelSecurity" label="Row Level Security" valuePropName="checked">
                <Switch disabled={isRlsDisabled} />
              </Form.Item>
            </div>
            {watchRls && (
              <div className="grid grid-cols-1 gap-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Form.Item name="roleName" label="Role Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="secondRoleName" label="Second Role Name">
                    <Input />
                  </Form.Item>
                </div>
              </div>
            )}
            {(reportGroupOptions.length > 0 || sortedDirectUserOptions.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Form.Item
                  name="groupIds"
                  label={renderFieldLabel('User Groups', isGroupOptionsLoading, 'Loading groups')}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select user groups"
                    options={reportGroupOptions.map((g) => ({
                      value: g.value,
                      label: g.name,
                    }))}
                    optionFilterProp="label"
                    disabled={isGroupOptionsLoading}
                    loading={isGroupOptionsLoading}
                    dropdownRender={(menu) => (
                      <>
                        {renderSelectAllMenu('groups', groupOptionValues, isGroupOptionsLoading, (values) => {
                          form.setFieldValue('groupIds', values)
                        })}
                        {menu}
                      </>
                    )}
                  />
                </Form.Item>
                <Form.Item
                  name="userIds"
                  label={renderFieldLabel('Direct Users', isUserOptionsLoading, 'Loading users')}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select direct users"
                    options={sortedDirectUserOptions.map((u: UserOption) => ({
                      value: u.value,
                      label: u.name,
                    }))}
                    optionFilterProp="label"
                    disabled={isUserOptionsLoading}
                    loading={isUserOptionsLoading}
                    dropdownRender={(menu) => (
                      <>
                        {renderSelectAllMenu('users', directUserOptionValues, isUserOptionsLoading, (values) => {
                          form.setFieldValue('userIds', values)
                        })}
                        {menu}
                      </>
                    )}
                  />
                </Form.Item>
              </div>
            )}
          </Form>
        )}
      </Modal>
    </FeatureShell>
  )
}
